import React, { useContext } from "react";
import { NavLink } from "react-router-dom";
import {
	LayoutDashboard,
	Repeat,
	MessageCircle,
	Contact,
	Network,
	MessagesSquare,
	Users,
	Settings,
	Tag,
	Megaphone,
	CalendarClock,
	Image,
	Users2,
	Building2,
	KanbanSquare,
	MessageSquareText,
	Workflow,
	BarChart3,
	ListChecks,
	Building,
	Receipt,
	Package,
	Puzzle,
	Shield,
	Sparkles,
	Activity,
	CircleDollarSign,
	BadgePercent,
	Bot,
	LifeBuoy,
	Plug,
	SlidersHorizontal,
	Quote,
	Sun,
	Moon,
	LogOut,
} from "lucide-react";

import { Sheet, SheetContent } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { i18n } from "../translate/i18n";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import useModules from "../hooks/useModules";
import { useThemeContext } from "../context/DarkMode";
import GlobalSearch from "../components/GlobalSearch";
import NotificationsPopOver from "../components/NotificationsPopOver";

// O menu completo do celular, em grade de ícones.
//
// A gaveta lateral é padrão de desktop: uma lista vertical de 38 linhas que,
// numa tela de 390px, obriga a rolar muito para achar qualquer coisa e cobre
// o conteúdo por um lado só. Em aplicativo de celular o "Mais" abre uma
// bandeja que sobe, com tudo à vista em ícones grandes — quatro por linha,
// alvo confortável para o dedo, e o rótulo embaixo para não virar adivinhação.
//
// As regras são as mesmas do menu lateral: quem não tem permissão não vê a
// seção, e o que está fora do plano aparece apagado (some era pior — quem não
// vê o recurso não sabe que ele existe e não tem o que pedir).

const Item = ({ to, icone: Icone, rotulo, foraDoPlano, aviso, aoNavegar }) => (
	<NavLink
		to={to}
		onClick={aoNavegar}
		title={
			foraDoPlano
				? `${rotulo} — não está no seu plano. Fale com quem administra a conta.`
				: undefined
		}
		className={({ isActive }) =>
			cn(
				// Altura fixa, igual para todos. Com altura automática, o único
				// rótulo que quebra em duas linhas ("Config. campanhas") deixava
				// a célula 14px mais alta e empurrava a linha inteira para
				// baixo, desalinhando a grade. Aqui todo item reserva as duas
				// linhas, use ou não.
				"flex h-[88px] flex-col items-center justify-start gap-1.5 rounded-xl p-1.5 text-center transition-colors",
				"active:bg-accent",
				isActive ? "bg-primary/10" : "hover:bg-accent",
				foraDoPlano && "opacity-40"
			)
		}
	>
		{({ isActive }) => (
			<>
				<span
					className={cn(
						"relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
						isActive
							? "bg-primary text-primary-foreground"
							: "bg-muted text-muted-foreground"
					)}
				>
					<Icone className="h-5 w-5" />
					{aviso && (
						<span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-destructive ring-2 ring-card" />
					)}
				</span>
				{/* Duas linhas no máximo: numa célula de 95px, um rótulo longo
				    sem limite empurraria a altura de novo. */}
				<span
					className={cn(
						"line-clamp-2 text-[11px] leading-tight",
						isActive ? "font-semibold text-primary" : "text-foreground"
					)}
				>
					{rotulo}
				</span>
			</>
		)}
	</NavLink>
);

const Secao = ({ titulo, children }) => (
	<div>
		<p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
			{titulo}
		</p>
		<div className="grid grid-cols-4 gap-1">{children}</div>
	</div>
);

const MenuDeIcones = ({ aberto, aoFechar, aoAbrirPerfil, aoSair }) => {
	const { user } = useContext(AuthContext);
	const { whatsApps } = useContext(WhatsAppsContext);
	const { temModulo } = useModules();
	const { darkMode: temaEscuro, toggleTheme: alternarTema } =
		useThemeContext();

	// Mesmo aviso do menu lateral: conexão caída é atendimento parado, e a
	// bolinha no ícone é o que faz alguém ir olhar.
	const conexaoComProblema = whatsApps?.some((w) =>
		["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(
			w.status
		)
	);

	const ehAdmin = user?.profile === "admin";
	const ehOperadora = Number(user?.companyId) === 1;

	const fechar = () => aoFechar();

	return (
		<Sheet open={aberto} onOpenChange={(v) => !v && aoFechar()}>
			<SheetContent
				side="bottom"
				// Quase a tela toda: menu que mostra metade obriga a rolar antes
				// de saber o que existe. A safe area evita que a última linha
				// caia atrás do indicador de início do iPhone.
				className="h-[88dvh] rounded-t-2xl p-0 md:hidden"
			>
				<div className="flex h-full flex-col">
					{/* Quem está usando e as ações da conta.
					    A barra de cima saiu do celular — ela roubava 56px de
					    altura em toda tela para mostrar coisas que se usam de vez
					    em quando. O que estava lá mora aqui: perfil, tema e sair. */}
					<div className="flex shrink-0 items-center gap-3 border-b px-4 pb-3 pt-4">
						<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold uppercase text-muted-foreground">
							{(user?.name || "?").slice(0, 2)}
						</span>
						<button
							type="button"
							className="min-w-0 flex-1 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
							onClick={() => {
								aoAbrirPerfil();
								fechar();
							}}
						>
							<p className="truncate text-sm font-semibold">{user?.name}</p>
							<p className="truncate text-xs text-muted-foreground">
								{user?.email}
							</p>
						</button>

						{/* Busca e avisos também vinham da barra de cima. Ficam na
						    linha da conta, sempre a um toque do "Mais". */}
						{user?.id && <GlobalSearch />}
						{user?.id && <NotificationsPopOver />}

						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 shrink-0"
							aria-label={temaEscuro ? "Tema claro" : "Tema escuro"}
							onClick={alternarTema}
						>
							{temaEscuro ? (
								<Sun className="h-5 w-5" />
							) : (
								<Moon className="h-5 w-5" />
							)}
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 shrink-0 text-destructive hover:text-destructive"
							aria-label="Sair"
							onClick={aoSair}
						>
							<LogOut className="h-5 w-5" />
						</Button>
					</div>

					<div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
						<Secao titulo="Atendimento">
							<Item
								to="/"
								icone={LayoutDashboard}
								rotulo="Dashboard"
								aoNavegar={fechar}
							/>
							<Item
								to="/tickets"
								icone={MessageCircle}
								rotulo={i18n.t("mainDrawer.listItems.tickets")}
								aoNavegar={fechar}
							/>
							<Item
								to="/contacts"
								icone={Contact}
								rotulo={i18n.t("mainDrawer.listItems.contacts")}
								aoNavegar={fechar}
							/>
							<Item
								to="/quickAnswers"
								icone={MessagesSquare}
								rotulo="Respostas"
								aoNavegar={fechar}
							/>
							<Item
								to="/media-library"
								icone={Image}
								rotulo="Mídias"
								foraDoPlano={!temModulo("mediaLibrary")}
								aoNavegar={fechar}
							/>
						</Secao>

						<Secao titulo="Automação">
							<Item
								to="/flows"
								icone={Workflow}
								rotulo="Fluxo de conversa"
								foraDoPlano={!temModulo("flows")}
								aoNavegar={fechar}
							/>
							<Item
								to="/scheduled-messages"
								icone={CalendarClock}
								rotulo="Agendadas"
								foraDoPlano={!temModulo("schedules")}
								aoNavegar={fechar}
							/>
							<Item
								to="/ai"
								icone={Sparkles}
								rotulo="I.A."
								aoNavegar={fechar}
							/>
						</Secao>

						<Secao titulo="Vendas">
							<Item
								to="/kanban"
								icone={KanbanSquare}
								rotulo="Kanban"
								foraDoPlano={!temModulo("crm") && !temModulo("kanban")}
								aoNavegar={fechar}
							/>
							<Item
								to="/campaigns"
								icone={Megaphone}
								rotulo="Campanhas"
								foraDoPlano={!temModulo("campaigns")}
								aoNavegar={fechar}
							/>
							<Item
								to="/contact-lists"
								icone={ListChecks}
								rotulo="Listas"
								foraDoPlano={!temModulo("campaigns")}
								aoNavegar={fechar}
							/>
							<Item
								to="/campaigns-phrases"
								icone={Quote}
								rotulo="Frases"
								foraDoPlano={!temModulo("campaigns")}
								aoNavegar={fechar}
							/>
							<Item
								to="/campaigns-config"
								icone={SlidersHorizontal}
								rotulo="Config. campanhas"
								foraDoPlano={!temModulo("campaigns")}
								aoNavegar={fechar}
							/>
						</Secao>

						<Secao titulo="Acompanhamento">
							<Item
								to="/operational-panel"
								icone={Activity}
								rotulo="Painel"
								aoNavegar={fechar}
							/>
							<Item
								to="/reports"
								icone={BarChart3}
								rotulo="Relatórios"
								foraDoPlano={!temModulo("reports")}
								aoNavegar={fechar}
							/>
						</Secao>

						<Secao titulo="Equipe">
							<Item
								to="/internal-chat"
								icone={MessageSquareText}
								rotulo="Chat Interno"
								foraDoPlano={!temModulo("internalChat")}
								aoNavegar={fechar}
							/>
							<Item
								to="/announcements"
								icone={Megaphone}
								rotulo="Avisos"
								aoNavegar={fechar}
							/>
							<Item
								to="/helps"
								icone={LifeBuoy}
								rotulo="Ajuda"
								aoNavegar={fechar}
							/>
						</Secao>

						{/* Configurações: mesma regra do menu lateral — só quem
						    administra. Para os outros, cada tela daqui devolveria
						    um 403. */}
						{ehAdmin && (
							<Secao titulo="Configurações">
								<Item
									to="/connections"
									icone={Repeat}
									rotulo={i18n.t("mainDrawer.listItems.connections")}
									aviso={conexaoComProblema}
									aoNavegar={fechar}
								/>
								<Item
									to="/Queues"
									icone={Network}
									rotulo={i18n.t("mainDrawer.listItems.queues")}
									aoNavegar={fechar}
								/>
								<Item
									to="/tags"
									icone={Tag}
									rotulo={i18n.t("mainDrawer.listItems.tags")}
									aoNavegar={fechar}
								/>
								<Item
									to="/users"
									icone={Users}
									rotulo={i18n.t("mainDrawer.listItems.users")}
									aoNavegar={fechar}
								/>
								<Item
									to="/roles"
									icone={Shield}
									rotulo="Cargos"
									aoNavegar={fechar}
								/>
								<Item
									to="/teams"
									icone={Users2}
									rotulo="Equipes"
									aoNavegar={fechar}
								/>
								<Item
									to="/departments"
									icone={Building2}
									rotulo="Departamentos"
									aoNavegar={fechar}
								/>
								<Item
									to="/queue-integrations"
									icone={Bot}
									rotulo="Integrações"
									aoNavegar={fechar}
								/>
								<Item
									to="/messages-api"
									icone={Plug}
									rotulo="Webhooks e API"
									foraDoPlano={!temModulo("webhooks")}
									aoNavegar={fechar}
								/>
								{ehOperadora && (
									<Item
										to="/global-config"
										icone={SlidersHorizontal}
										rotulo="Config. globais"
										aoNavegar={fechar}
									/>
								)}
								{!ehOperadora && (
									<Item
										to="/financeiro"
										icone={CircleDollarSign}
										rotulo="Financeiro"
										aoNavegar={fechar}
									/>
								)}
								<Item
									to="/Settings"
									icone={Settings}
									rotulo={i18n.t("mainDrawer.listItems.settings")}
									aoNavegar={fechar}
								/>
							</Secao>
						)}

						{/* Gestão do SaaS: só o admin da empresa operadora. */}
						{ehAdmin && ehOperadora && (
							<Secao titulo="Gestão SaaS">
								<Item
									to="/saas/finance"
									icone={CircleDollarSign}
									rotulo="Financeiro"
									aoNavegar={fechar}
								/>
								<Item
									to="/companies"
									icone={Building}
									rotulo="Empresas"
									aoNavegar={fechar}
								/>
								<Item
									to="/plans"
									icone={Package}
									rotulo="Planos"
									aoNavegar={fechar}
								/>
								<Item
									to="/modules"
									icone={Puzzle}
									rotulo="Módulos"
									aoNavegar={fechar}
								/>
								<Item
									to="/saas/commercial"
									icone={BadgePercent}
									rotulo="Comercial"
									aoNavegar={fechar}
								/>
								<Item
									to="/invoices"
									icone={Receipt}
									rotulo="Faturas"
									aoNavegar={fechar}
								/>
							</Secao>
						)}
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
};

export default MenuDeIcones;
